import { AsyncActionCreator } from '../types';
import { createAsyncAction } from '../utils';
import {
  ActionType,
  ClearServicePlanPeriods,
  ClearServicePlans,
  ClearServicePlanSubscriptions,
  CreateDiscountCode,
  CreateServicePlan,
  CreateServicePlanCommitment,
  CreateServicePlanPrice,
  CreateServicePlansCustomerReminder,
  DeleteServicePlan,
  DeleteServicePlanCommitment,
  DeleteServicePlanDiscountCode,
  DeleteServicePlanPeriodCommitment,
  DeleteServicePlanPrice,
  DeleteServicePlansCustomerReminder,
  GetListedDiscountCodes,
  GetListedServicePlans,
  GetServicePeriods,
  GetServicePlan,
  GetServicePlanDiscountCodes,
  GetServicePlanPeriod,
  GetServicePlansAnalytics,
  GetServicePlansCustomerReminder,
  GetServicePlansCustomerReminders,
  GetServicePlanSubscription,
  GetSubscriptionsForCustomer,
  GetSubscriptionsForServicePlan,
  InviteCustomerToServicePlan,
  ToggleServicePlansCustomerReminder,
  UpdateServicePlan,
  UpdateServicePlanCommitment,
  UpdateServicePlanPeriod,
  UpdateServicePlanPeriodCommitment,
  UpdateServicePlanPrice,
  UpdateServicePlansCustomerReminder,
} from './servicePlansTypes';

/**
 * Get listed service plans discount codes for an account
 */
export const getListedDiscountCodes: AsyncActionCreator<
  GetListedDiscountCodes,
  (payload: GetListedDiscountCodes['request']['payload']) => {
    payload: GetListedDiscountCodes['request']['payload'];
  }
> = createAsyncAction<GetListedDiscountCodes>({
  request: ActionType.GET_LISTED_DISCOUNT_CODES_REQUEST,
  success: ActionType.GET_LISTED_DISCOUNT_CODES_SUCCESS,
  failure: ActionType.GET_LISTED_DISCOUNT_CODES_FAILURE,
})((payload: GetListedDiscountCodes['request']['payload']) => ({ payload }));

/**
 * Get listed service plans for an account - i.e. the service plans a user has created and available for their customer to purchase
 */
export const getListedServicePlans: AsyncActionCreator<
  GetListedServicePlans,
  (payload: GetListedServicePlans['request']['payload']) => {
    payload: GetListedServicePlans['request']['payload'];
  }
> = createAsyncAction<GetListedServicePlans>({
  request: ActionType.GET_LISTED_SERVICE_PLANS_REQUEST,
  success: ActionType.GET_LISTED_SERVICE_PLANS_SUCCESS,
  failure: ActionType.GET_LISTED_SERVICE_PLANS_FAILURE,
})((payload: GetListedServicePlans['request']['payload']) => ({ payload }));

/**
 * Get service periods - details of customers subscribed plans
 */
export const getServicePeriods: AsyncActionCreator<
  GetServicePeriods,
  (payload: GetServicePeriods['request']['payload']) => {
    payload: GetServicePeriods['request']['payload'];
  }
> = createAsyncAction<GetServicePeriods>({
  request: ActionType.GET_SERVICE_PERIODS_REQUEST,
  success: ActionType.GET_SERVICE_PERIODS_SUCCESS,
  failure: ActionType.GET_SERVICE_PERIODS_FAILURE,
})((payload: GetServicePeriods['request']['payload']) => ({ payload }));

/**
 * Get service plans analytics
 */
export const getServicePlansAnalytics: AsyncActionCreator<
  GetServicePlansAnalytics,
  (payload: GetServicePlansAnalytics['request']['payload']) => {
    payload: GetServicePlansAnalytics['request']['payload'];
  }
> = createAsyncAction<GetServicePlansAnalytics>({
  request: ActionType.GET_SERVICE_PLANS_ANALYTICS_REQUEST,
  success: ActionType.GET_SERVICE_PLANS_ANALYTICS_SUCCESS,
  failure: ActionType.GET_SERVICE_PLANS_ANALYTICS_FAILURE,
})((payload: GetServicePlansAnalytics['request']['payload']) => ({
  payload,
}));

/**
 * Get service plan - details of a service plan which a user has created
 */
export const getServicePlan: AsyncActionCreator<
  GetServicePlan,
  (payload: GetServicePlan['request']['payload']) => {
    payload: GetServicePlan['request']['payload'];
    meta: { publicId: GetServicePlan['request']['payload']['publicId'] };
  }
> = createAsyncAction<GetServicePlan>({
  request: ActionType.GET_SERVICE_PLAN_REQUEST,
  success: ActionType.GET_SERVICE_PLAN_SUCCESS,
  failure: ActionType.GET_SERVICE_PLAN_FAILURE,
})((payload: GetServicePlan['request']['payload']) => ({
  payload,
  meta: { publicId: payload.publicId },
}));

/**
 * Create service plan
 */
export const createServicePlan: AsyncActionCreator<
  CreateServicePlan,
  (payload: CreateServicePlan['request']['payload']) => {
    payload: CreateServicePlan['request']['payload'];
  }
> = createAsyncAction<CreateServicePlan>({
  request: ActionType.CREATE_SERVICE_PLAN_REQUEST,
  success: ActionType.CREATE_SERVICE_PLAN_SUCCESS,
  failure: ActionType.CREATE_SERVICE_PLAN_FAILURE,
})((payload: CreateServicePlan['request']['payload']) => ({
  payload,
}));

/**
 * Update service plan
 */
export const updateServicePlan: AsyncActionCreator<
  UpdateServicePlan,
  (payload: UpdateServicePlan['request']['payload']) => {
    payload: UpdateServicePlan['request']['payload'];
  }
> = createAsyncAction<UpdateServicePlan>({
  request: ActionType.UPDATE_SERVICE_PLAN_REQUEST,
  success: ActionType.UPDATE_SERVICE_PLAN_SUCCESS,
  failure: ActionType.UPDATE_SERVICE_PLAN_FAILURE,
})((payload: UpdateServicePlan['request']['payload']) => ({
  payload,
}));

/**
 * Delete service plan
 */
export const deleteServicePlan = createAsyncAction<DeleteServicePlan>({
  request: ActionType.DELETE_SERVICE_PLAN_REQUEST,
  success: ActionType.DELETE_SERVICE_PLAN_SUCCESS,
  failure: ActionType.DELETE_SERVICE_PLAN_FAILURE,
})((payload: DeleteServicePlan['request']['payload']) => ({
  payload,
}));

/**
 * Delete service plan discount code
 */
export const deleteServicePlanDiscountCode =
  createAsyncAction<DeleteServicePlanDiscountCode>({
    request: ActionType.DELETE_SERVICE_PLAN_DISCOUNT_CODE_REQUEST,
    success: ActionType.DELETE_SERVICE_PLAN_DISCOUNT_CODE_SUCCESS,
    failure: ActionType.DELETE_SERVICE_PLAN_DISCOUNT_CODE_FAILURE,
  })((payload: DeleteServicePlanDiscountCode['request']['payload']) => ({
    payload,
  }));

/**
 * Get discount codes by servicePlanPublicId
 */
export const getServicePlanDiscountCodes: AsyncActionCreator<
  GetServicePlanDiscountCodes,
  (payload: GetServicePlanDiscountCodes['request']['payload']) => {
    payload: GetServicePlanDiscountCodes['request']['payload'];
    meta: {
      publicId: GetServicePlanDiscountCodes['request']['payload']['publicId'];
    };
  }
> = createAsyncAction<GetServicePlanDiscountCodes>({
  request: ActionType.GET_SERVICE_PLAN_DISCOUNT_CODES_REQUEST,
  success: ActionType.GET_SERVICE_PLAN_DISCOUNT_CODES_SUCCESS,
  failure: ActionType.GET_SERVICE_PLAN_DISCOUNT_CODES_FAILURE,
})((payload: GetServicePlanDiscountCodes['request']['payload']) => ({
  payload,
  meta: { publicId: payload.publicId },
}));

/**
 * Create service plan discount code
 */
export const createDiscountCode: AsyncActionCreator<
  CreateDiscountCode,
  (payload: CreateDiscountCode['request']['payload']) => {
    payload: CreateDiscountCode['request']['payload'];
  }
> = createAsyncAction<CreateDiscountCode>({
  request: ActionType.CREATE_DISCOUNT_CODE_REQUEST,
  success: ActionType.CREATE_DISCOUNT_CODE_SUCCESS,
  failure: ActionType.CREATE_DISCOUNT_CODE_FAILURE,
})((payload: CreateDiscountCode['request']['payload']) => ({
  payload,
}));

/**
 * Send invite service plan email to customer
 */

export const inviteCustomerToServicePlan: AsyncActionCreator<
  InviteCustomerToServicePlan,
  (payload: InviteCustomerToServicePlan['request']['payload']) => {
    payload: InviteCustomerToServicePlan['request']['payload'];
  }
> = createAsyncAction<InviteCustomerToServicePlan>({
  request: ActionType.INVITE_CUSTOMER_TO_SERVICE_PLAN_REQUEST,
  success: ActionType.INVITE_CUSTOMER_TO_SERVICE_PLAN_SUCCESS,
  failure: ActionType.INVITE_CUSTOMER_TO_SERVICE_PLAN_FAILURE,
})((payload: InviteCustomerToServicePlan['request']['payload']) => ({
  payload,
}));

export const updateServicePlanCommitment: AsyncActionCreator<
  UpdateServicePlanCommitment,
  (payload: UpdateServicePlanCommitment['request']['payload']) => {
    payload: UpdateServicePlanCommitment['request']['payload'];
  }
> = createAsyncAction<UpdateServicePlanCommitment>({
  request: ActionType.UPDATE_SERVICE_PLAN_COMMITMENT_REQUEST,
})((payload: UpdateServicePlanCommitment['request']['payload']) => ({
  payload,
}));

export const createServicePlanCommitment: AsyncActionCreator<
  CreateServicePlanCommitment,
  (payload: CreateServicePlanCommitment['request']['payload']) => {
    payload: CreateServicePlanCommitment['request']['payload'];
  }
> = createAsyncAction<CreateServicePlanCommitment>({
  request: ActionType.CREATE_SERVICE_PLAN_COMMITMENT_REQUEST,
})((payload: CreateServicePlanCommitment['request']['payload']) => ({
  payload,
}));

export const deleteServicePlanCommitment =
  createAsyncAction<DeleteServicePlanCommitment>({
    request: ActionType.DELETE_SERVICE_PLAN_COMMITMENT_REQUEST,
  })((payload: DeleteServicePlanCommitment['request']['payload']) => ({
    payload,
  }));

export const getSubscriptionsForServicePlan: AsyncActionCreator<
  GetSubscriptionsForServicePlan,
  (payload: GetSubscriptionsForServicePlan['request']['payload']) => {
    payload: GetSubscriptionsForServicePlan['request']['payload'];
  }
> = createAsyncAction<GetSubscriptionsForServicePlan>({
  request: ActionType.GET_SUBSCRIPTIONS_FOR_SERVICE_PLAN_REQUEST,
  success: ActionType.GET_SUBSCRIPTIONS_FOR_SERVICE_PLAN_SUCCESS,
  failure: ActionType.GET_SUBSCRIPTIONS_FOR_SERVICE_PLAN_FAILURE,
})((payload: GetSubscriptionsForServicePlan['request']['payload']) => ({
  payload,
}));

export const getSubscriptionsForCustomer: AsyncActionCreator<
  GetSubscriptionsForCustomer,
  (payload: GetSubscriptionsForCustomer['request']['payload']) => {
    payload: GetSubscriptionsForCustomer['request']['payload'];
  }
> = createAsyncAction<GetSubscriptionsForCustomer>({
  request: ActionType.GET_SUBSCRIPTIONS_FOR_CUSTOMER_REQUEST,
  success: ActionType.GET_SUBSCRIPTIONS_FOR_CUSTOMER_SUCCESS,
  failure: ActionType.GET_SUBSCRIPTIONS_FOR_CUSTOMER_FAILURE,
})((payload: GetSubscriptionsForCustomer['request']['payload']) => ({
  payload,
}));

export const clearServicePlanSubscriptions =
  createAsyncAction<ClearServicePlanSubscriptions>({
    request: ActionType.CLEAR_SERVICE_PLAN_SUBSCRIPTIONS,
  })();

export const clearServicePlans = createAsyncAction<ClearServicePlans>({
  request: ActionType.CLEAR_SERVICE_PLANS,
})();

export const clearServicePlanPeriods =
  createAsyncAction<ClearServicePlanPeriods>({
    request: ActionType.CLEAR_SERVICE_PLAN_PERIODS,
  })();

export const updateServicePlanPrice: AsyncActionCreator<
  UpdateServicePlanPrice,
  (payload: UpdateServicePlanPrice['request']['payload']) => {
    payload: UpdateServicePlanPrice['request']['payload'];
  }
> = createAsyncAction<UpdateServicePlanPrice>({
  request: ActionType.UPDATE_SERVICE_PLAN_PRICE_REQUEST,
})((payload: UpdateServicePlanPrice['request']['payload']) => ({
  payload,
}));

export const deleteServicePlanPrice = createAsyncAction<DeleteServicePlanPrice>(
  {
    request: ActionType.DELETE_SERVICE_PLAN_PRICE_REQUEST,
  }
)((payload: DeleteServicePlanPrice['request']['payload']) => ({
  payload,
}));

export const updateServicePlanPeriodCommitment =
  createAsyncAction<UpdateServicePlanPeriodCommitment>({
    request: ActionType.UPDATE_SERVICE_PLAN_PERIOD_COMMITMENT_REQUEST,
  })((payload: UpdateServicePlanPeriodCommitment['request']['payload']) => ({
    payload,
  }));

export const createServicePlanPrice: AsyncActionCreator<
  CreateServicePlanPrice,
  (payload: CreateServicePlanPrice['request']['payload']) => {
    payload: CreateServicePlanPrice['request']['payload'];
  }
> = createAsyncAction<CreateServicePlanPrice>({
  request: ActionType.CREATE_SERVICE_PLAN_PRICE_REQUEST,
})((payload: CreateServicePlanPrice['request']['payload']) => ({
  payload,
}));

/**
 * Get service plan period
 */
export const getServicePlanPeriod: AsyncActionCreator<
  GetServicePlanPeriod,
  (payload: GetServicePlanPeriod['request']['payload']) => {
    payload: GetServicePlanPeriod['request']['payload'];
    meta: { publicId: GetServicePlanPeriod['request']['payload']['publicId'] };
  }
> = createAsyncAction<GetServicePlanPeriod>({
  request: ActionType.GET_SERVICE_PLAN_PERIOD_REQUEST,
  success: ActionType.GET_SERVICE_PLAN_PERIOD_SUCCESS,
  failure: ActionType.GET_SERVICE_PLAN_PERIOD_FAILURE,
})((payload: GetServicePlanPeriod['request']['payload']) => ({
  payload,
  meta: { publicId: payload.publicId },
}));

export const deleteServicePlanPeriodCommitment =
  createAsyncAction<DeleteServicePlanPeriodCommitment>({
    request: ActionType.DELETE_SERVICE_PLAN_PERIOD_COMMITMENT_REQUEST,
  })((payload: DeleteServicePlanPeriodCommitment['request']['payload']) => ({
    payload,
  }));

export const updateServicePlanPeriod =
  createAsyncAction<UpdateServicePlanPeriod>({
    request: ActionType.UPDATE_SERVICE_PLAN_PERIOD_REQUEST,
  })((payload: UpdateServicePlanPeriod['request']['payload']) => ({
    payload,
  }));

export const getServicePlanSubscription: AsyncActionCreator<
  GetServicePlanSubscription,
  (payload: GetServicePlanSubscription['request']['payload']) => {
    payload: GetServicePlanSubscription['request']['payload'];
  }
> = createAsyncAction<GetServicePlanSubscription>({
  request: ActionType.GET_SERVICE_PLAN_SUBSCRIPTION_REQUEST,
  success: ActionType.GET_SERVICE_PLAN_SUBSCRIPTION_SUCCESS,
  failure: ActionType.GET_SERVICE_PLAN_SUBSCRIPTION_FAILURE,
})((payload: GetServicePlanSubscription['request']['payload']) => ({
  payload,
}));

export const getServicePlansCustomerReminder: AsyncActionCreator<
  GetServicePlansCustomerReminder,
  (payload: GetServicePlansCustomerReminder['request']['payload']) => {
    payload: GetServicePlansCustomerReminder['request']['payload'];
  }
> = createAsyncAction<GetServicePlansCustomerReminder>({
  request: ActionType.GET_SERVICE_PLANS_CUSTOMER_REMINDER_REQUEST,
})((payload: GetServicePlansCustomerReminder['request']['payload']) => ({
  payload,
}));

export const getServicePlansCustomerReminders: AsyncActionCreator<
  GetServicePlansCustomerReminders,
  (payload: GetServicePlansCustomerReminders['request']['payload']) => {
    payload: GetServicePlansCustomerReminders['request']['payload'];
  }
> = createAsyncAction<GetServicePlansCustomerReminders>({
  request: ActionType.GET_SERVICE_PLANS_CUSTOMER_REMINDERS_REQUEST,
  success: ActionType.GET_SERVICE_PLANS_CUSTOMER_REMINDERS_SUCCESS,
})((payload: GetServicePlansCustomerReminders['request']['payload']) => ({
  payload,
}));

export const toggleServicePlansCustomerReminder: AsyncActionCreator<
  ToggleServicePlansCustomerReminder,
  (payload: ToggleServicePlansCustomerReminder['request']['payload']) => {
    payload: ToggleServicePlansCustomerReminder['request']['payload'];
  }
> = createAsyncAction<ToggleServicePlansCustomerReminder>({
  request: ActionType.TOGGLE_SERVICE_PLANS_CUSTOMER_REMINDER_REQUEST,
  success: ActionType.TOGGLE_SERVICE_PLANS_CUSTOMER_REMINDER_SUCCESS,
})((payload: ToggleServicePlansCustomerReminder['request']['payload']) => ({
  payload,
}));

export const createServicePlansCustomerReminder: AsyncActionCreator<
  CreateServicePlansCustomerReminder,
  (payload: CreateServicePlansCustomerReminder['request']['payload']) => {
    payload: CreateServicePlansCustomerReminder['request']['payload'];
  }
> = createAsyncAction<CreateServicePlansCustomerReminder>({
  request: ActionType.CREATE_SERVICE_PLANS_CUSTOMER_REMINDER_REQUEST,
})((payload: CreateServicePlansCustomerReminder['request']['payload']) => ({
  payload,
}));

export const updateServicePlansCustomerReminder: AsyncActionCreator<
  UpdateServicePlansCustomerReminder,
  (payload: UpdateServicePlansCustomerReminder['request']['payload']) => {
    payload: UpdateServicePlansCustomerReminder['request']['payload'];
  }
> = createAsyncAction<UpdateServicePlansCustomerReminder>({
  request: ActionType.UPDATE_SERVICE_PLANS_CUSTOMER_REMINDER_REQUEST,
})((payload: UpdateServicePlansCustomerReminder['request']['payload']) => ({
  payload,
}));

export const deleteServicePlansCustomerReminder =
  createAsyncAction<DeleteServicePlansCustomerReminder>({
    request: ActionType.DELETE_SERVICE_PLANS_CUSTOMER_REMINDER_REQUEST,
  })((payload: DeleteServicePlansCustomerReminder['request']['payload']) => ({
    payload,
  }));
